import { useEffect, useState } from 'react';
import {
  CompatibilityCheckQuestionToAnswer,
  leaseFundWebQuestionList,
  SelectAndTextAnswer,
  CompatibilityCheckAnswer,
  CompatibilityCheckQuestion,
  personalDomesticRealEstateWebQuestionList,
  MultiSelectionAnswer,
  corporateDomesticRealEstateWebQuestionList,
  overseasRealEstateWebQuestionList,
} from 'views/pages/compatibilityCheck/hooks';
import { GetCompatibilityQuery, useGetCompatibilityQuery } from 'views/utils/graphql/graphql';
import {
  AmountRange,
  convertEnumToMultiSelectionAnswer,
  CorporatePurposeCompatibilityStrs,
  CorporatePurposeCompatibilityStrsExcludeOther,
  InvestmentRange,
  PersonalPurposeCompatibilityStrs,
} from 'views/utils/enums';
import { ProductType } from 'constants/productType';

export enum CompatibilityCheckConditionType {
  ApprovalRequired = 1, // 承認が必要
  ExpirationCheck = 2, // 有効期限の確認が必要
}

const leaseFundInPersonQuestionList: CompatibilityCheckQuestion[] = [
  {
    kind: 'yesOrNo',
    key: 'q1',
    questionText: `外貨建て金融商品への投資経験はありますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q2',
    questionText: `信用リスクがある金融商品への投資経験はありますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q3',
    questionText: `市場価値が変動するリスクのある金融商品への投資経験はありますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q4',
    questionText: `日本型オペレーティング・リース商品への投資経験はありますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q5',
    questionText: `本件投資の仕組みやリスクについて理解していますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q6',
    questionText: `本件投資資金は、投資期間中に他に使用する必要がない資金ですか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q7',
    questionText: `本件投資は中途解約できないことを認識していますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q8',
    questionText: `本件投資について、元本毀損の可能性（および場合によっては追加出資が必要となる可能性）があることを認識していますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q9',
    questionText: `本件投資の目的として、もっとも重視されるものは何ですか。`,
  },
];

const domesticRealEstateInPersonQuestionList: CompatibilityCheckQuestion[] = [
  {
    kind: 'yesOrNo',
    key: 'q1',
    questionText: `不動産投資の経験はありますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q2',
    questionText: `不動産小口化商品（不動産信託受益権、不動産特定共同事業）への投資経験はありますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q3',
    questionText: `不動産小口化商品の仕組みやリスクについて理解していますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q4',
    questionText: `保有する金融資産の状況についてご教示ください。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q5',
    questionText: `ご年収についてご教示ください。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q6',
    questionText: `本件投資資金は、投資期間中に他に使用する必要がない資金ですか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q7',
    questionText: `本件投資について、投資損失発生の可能性があることを認識していますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q8',
    questionText: `本件投資の目的として、もっとも重視されるものは何ですか。（複数回答）`,
  },
];

const overseasRealEstateInPersonQuestionList: CompatibilityCheckQuestion[] = [
  {
    kind: 'yesOrNo',
    key: 'q1',
    questionText: `外貨建て金融商品への投資経験はありますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q2',
    questionText: `海外不動産への投資経験がありますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q3',
    questionText: `国内不動産への投資経験がありますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q4',
    questionText: `投資事業組合(任意組合、匿名組合、投資事業有限責任組合等)への投資経験がありますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q5',
    questionText: `任意組合の仕組みを理解していますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q6',
    questionText: `本件投資の仕組みやリスクについて理解していますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q7',
    questionText: `本件投資資金は、投資期間中に他に使用する必要がない資金ですか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q8',
    questionText: `本件投資は原則として中途解約できないことを認識していますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q9',
    questionText: `本件投資について、元本毀損の可能性(および場合によっては追加出資が必要となる可能性)があることを認識していますか。`,
  },
  {
    kind: 'yesOrNo',
    key: 'q10',
    questionText: `本件投資の目的として、もっとも重視されるものは何ですか。(複数回答)`,
  },
];

export enum ResponseType {
  LeaseFund_Web = 1,
  LeaseFund_InPerson,
  DomesticRealEstate_Personal_Web,
  DomesticRealEstate_Corporate_Web,
  DomesticRealEstate_InPerson,
  OverseasRealEstate_Web,
  OverseasRealEstate_InPerson,
}

const getValidResponseType = (data?: GetCompatibilityQuery): ResponseType | null => {
  const leaseFundWeb = data?.GetCompatibility?.LeaseFundAnswer?.WebAnswer;
  const leaseFundInPerson = data?.GetCompatibility?.LeaseFundAnswer?.InPersonAnswer;
  const domesticRealEstateCorporateWeb = data?.GetCompatibility?.DomesticRealEstateAnswer?.CorporateWebAnswer;
  const domesticRealEstatePersonalWeb = data?.GetCompatibility?.DomesticRealEstateAnswer?.PersonalWebAnswer;
  const domesticRealEstateInPerson = data?.GetCompatibility?.DomesticRealEstateAnswer?.InPersonAnswer;
  const overseasRealEstateWeb = data?.GetCompatibility?.OverseasRealEstateAnswer?.WebAnswer;
  const overseasRealEstateInPerson = data?.GetCompatibility?.OverseasRealEstateAnswer?.InPersonAnswer;

  const validResponses = [
    { answer: leaseFundWeb, type: ResponseType.LeaseFund_Web },
    { answer: leaseFundInPerson, type: ResponseType.LeaseFund_InPerson },
    { answer: domesticRealEstateCorporateWeb, type: ResponseType.DomesticRealEstate_Corporate_Web },
    { answer: domesticRealEstatePersonalWeb, type: ResponseType.DomesticRealEstate_Personal_Web },
    { answer: domesticRealEstateInPerson, type: ResponseType.DomesticRealEstate_InPerson },
    { answer: overseasRealEstateWeb, type: ResponseType.OverseasRealEstate_Web },
    { answer: overseasRealEstateInPerson, type: ResponseType.OverseasRealEstate_InPerson },
  ].filter((entry) => entry.answer != null);

  //レスポンスが無い、若しくは2つ以上ある場合不正
  if (validResponses.length !== 1) {
    return null;
  }

  return validResponses[0].type;
};

const createSetAnswersValue = (data: GetCompatibilityQuery, responseType: ResponseType): SetAnswersValue => {
  switch (responseType) {
    case ResponseType.LeaseFund_Web:
      const lease_web_q1 = data.GetCompatibility?.LeaseFundAnswer!.WebAnswer!.Question1;
      const lease_web_q2 = data.GetCompatibility?.LeaseFundAnswer!.WebAnswer!.Question2;
      const lease_web_q3 = data.GetCompatibility?.LeaseFundAnswer!.WebAnswer!.Question3;
      const lease_web_q4 = data.GetCompatibility?.LeaseFundAnswer!.WebAnswer!.Question4;
      const lease_web_q5 = data.GetCompatibility?.LeaseFundAnswer!.WebAnswer!.Question5;
      const lease_web_q6 = data.GetCompatibility?.LeaseFundAnswer!.WebAnswer!.Question6;
      const lease_web_q7 = data.GetCompatibility?.LeaseFundAnswer!.WebAnswer!.Question7;
      const lease_web_q8: SelectAndTextAnswer = {
        values:
          data.GetCompatibility?.LeaseFundAnswer!.WebAnswer!.Question8?.map((x) =>
            x !== null ? { value: x, label: CorporatePurposeCompatibilityStrs[x - 1] } : { value: -1, label: '' },
          ) ?? [],
        text: data.GetCompatibility?.LeaseFundAnswer!.WebAnswer!.Question8OtherText ?? '',
      };
      return {
        questionList: leaseFundWebQuestionList,
        answerList: [
          lease_web_q1,
          lease_web_q2,
          lease_web_q3,
          lease_web_q4,
          lease_web_q5,
          lease_web_q6,
          lease_web_q7,
          lease_web_q8,
        ],
      } as SetAnswersValue;
    case ResponseType.LeaseFund_InPerson:
      const lease_inPerson_q1 = data.GetCompatibility?.LeaseFundAnswer!.InPersonAnswer!.Question1Str;
      const lease_inPerson_q2 = data.GetCompatibility?.LeaseFundAnswer!.InPersonAnswer!.Question2Str;
      const lease_inPerson_q3 = data.GetCompatibility?.LeaseFundAnswer!.InPersonAnswer!.Question3Str;
      const lease_inPerson_q4 = data.GetCompatibility?.LeaseFundAnswer!.InPersonAnswer!.Question4Str;
      const lease_inPerson_q5 = data.GetCompatibility?.LeaseFundAnswer!.InPersonAnswer!.Question5Str;
      const lease_inPerson_q6 = data.GetCompatibility?.LeaseFundAnswer!.InPersonAnswer!.Question6Str;
      const lease_inPerson_q7 = data.GetCompatibility?.LeaseFundAnswer!.InPersonAnswer!.Question7Str;
      const lease_inPerson_q8 = data.GetCompatibility?.LeaseFundAnswer!.InPersonAnswer!.Question8Str;
      const lease_inPerson_q9 = `${data.GetCompatibility?.LeaseFundAnswer!.InPersonAnswer!.Question9Str}\n${
        data.GetCompatibility?.LeaseFundAnswer!.InPersonAnswer!.Question9OtherText
      }`;
      return {
        questionList: leaseFundInPersonQuestionList,
        answerList: [
          lease_inPerson_q1,
          lease_inPerson_q2,
          lease_inPerson_q3,
          lease_inPerson_q4,
          lease_inPerson_q5,
          lease_inPerson_q6,
          lease_inPerson_q7,
          lease_inPerson_q8,
          lease_inPerson_q9,
        ],
      } as SetAnswersValue;
    case ResponseType.DomesticRealEstate_Personal_Web:
      const domestic_personal_web_q1 = data.GetCompatibility?.DomesticRealEstateAnswer!.PersonalWebAnswer!.Question1;
      const domestic_personal_web_q2 = data.GetCompatibility?.DomesticRealEstateAnswer!.PersonalWebAnswer!.Question2;
      const domestic_personal_web_q3 = data.GetCompatibility?.DomesticRealEstateAnswer!.PersonalWebAnswer!.Question3;
      const domestic_personal_web_q4 = data.GetCompatibility?.DomesticRealEstateAnswer!.PersonalWebAnswer!.Question4;
      const domestic_personal_web_q5 = data.GetCompatibility?.DomesticRealEstateAnswer!.PersonalWebAnswer!.Question5;
      const domestic_personal_web_resQ6 = data.GetCompatibility?.DomesticRealEstateAnswer!.PersonalWebAnswer!.Question6;
      const domestic_personal_web_q6: MultiSelectionAnswer =
        domestic_personal_web_resQ6 != null
          ? convertEnumToMultiSelectionAnswer(InvestmentRange)[domestic_personal_web_resQ6 - 1]
          : ({ value: 0, label: '' } as MultiSelectionAnswer);
      const domestic_personal_web_resQ7 = data.GetCompatibility?.DomesticRealEstateAnswer!.PersonalWebAnswer!.Question7;
      const domestic_personal_web_q7: MultiSelectionAnswer =
        domestic_personal_web_resQ7 != null
          ? convertEnumToMultiSelectionAnswer(AmountRange)[domestic_personal_web_resQ7 - 1]
          : ({ value: 0, label: '' } as MultiSelectionAnswer);
      const domestic_personal_web_q8 = data.GetCompatibility?.DomesticRealEstateAnswer!.PersonalWebAnswer!.Question8;
      const domestic_personal_web_q9: SelectAndTextAnswer = {
        values:
          data.GetCompatibility?.DomesticRealEstateAnswer!.PersonalWebAnswer!.Question9?.map((x) =>
            x !== null ? { value: x, label: PersonalPurposeCompatibilityStrs[x - 1] } : { value: -1, label: '' },
          ) ?? [],
        text: '',
      };
      return {
        questionList: personalDomesticRealEstateWebQuestionList,
        answerList: [
          domestic_personal_web_q1,
          domestic_personal_web_q2,
          domestic_personal_web_q3,
          domestic_personal_web_q4,
          domestic_personal_web_q5,
          domestic_personal_web_q6,
          domestic_personal_web_q7,
          domestic_personal_web_q8,
          domestic_personal_web_q9,
        ],
      } as SetAnswersValue;
    case ResponseType.DomesticRealEstate_Corporate_Web:
      const domestic_corporate_web_q1 = data.GetCompatibility?.DomesticRealEstateAnswer!.CorporateWebAnswer!.Question1;
      const domestic_corporate_web_q2 = data.GetCompatibility?.DomesticRealEstateAnswer!.CorporateWebAnswer!.Question2;
      const domestic_corporate_web_q3 = data.GetCompatibility?.DomesticRealEstateAnswer!.CorporateWebAnswer!.Question3;
      const domestic_corporate_web_q4 = data.GetCompatibility?.DomesticRealEstateAnswer!.CorporateWebAnswer!.Question4;
      const domestic_corporate_web_q5 = data.GetCompatibility?.DomesticRealEstateAnswer!.CorporateWebAnswer!.Question5;
      const domestic_corporate_web_q6 = data.GetCompatibility?.DomesticRealEstateAnswer!.CorporateWebAnswer!.Question6;
      const domestic_corporate_web_q7: SelectAndTextAnswer = {
        values:
          data.GetCompatibility?.DomesticRealEstateAnswer!.CorporateWebAnswer!.Question7?.map((x) =>
            x !== null
              ? { value: x, label: CorporatePurposeCompatibilityStrsExcludeOther[x - 1] }
              : { value: -1, label: '' },
          ) ?? [],
        text: '',
      };
      return {
        questionList: corporateDomesticRealEstateWebQuestionList,
        answerList: [
          domestic_corporate_web_q1,
          domestic_corporate_web_q2,
          domestic_corporate_web_q3,
          domestic_corporate_web_q4,
          domestic_corporate_web_q5,
          domestic_corporate_web_q6,
          domestic_corporate_web_q7,
        ],
      } as SetAnswersValue;
    case ResponseType.DomesticRealEstate_InPerson:
      const domestic_inPerson_q1 = data.GetCompatibility?.DomesticRealEstateAnswer!.InPersonAnswer!.Question1Str;
      const domestic_inPerson_q2 = data.GetCompatibility?.DomesticRealEstateAnswer!.InPersonAnswer!.Question2Str;
      const domestic_inPerson_q3 = data.GetCompatibility?.DomesticRealEstateAnswer!.InPersonAnswer!.Question3Str;
      const domestic_inPerson_q4 = data.GetCompatibility?.DomesticRealEstateAnswer!.InPersonAnswer!.Question4Str;
      const domestic_inPerson_q5 = data.GetCompatibility?.DomesticRealEstateAnswer!.InPersonAnswer!.Question5Str;
      const domestic_inPerson_q6 = data.GetCompatibility?.DomesticRealEstateAnswer!.InPersonAnswer!.Question6Str;
      const domestic_inPerson_q7 = data.GetCompatibility?.DomesticRealEstateAnswer!.InPersonAnswer!.Question7Str;
      const domestic_inPerson_q8 = `${data.GetCompatibility?.DomesticRealEstateAnswer!.InPersonAnswer!.Question8Str}\n${
        data.GetCompatibility?.DomesticRealEstateAnswer!.InPersonAnswer!.Question8OtherText
      }`;

      const answerList = [
        domestic_inPerson_q1,
        domestic_inPerson_q2,
        domestic_inPerson_q3,
        domestic_inPerson_q4,
        domestic_inPerson_q5,
        domestic_inPerson_q6,
        domestic_inPerson_q7,
        domestic_inPerson_q8,
      ];

      // 国内不動産の対面の適合性チェックは、個人法人で表示させるものが違うだけ(個人:8、法人:6)
      // なので、回答がないものは表示させないことで、出し分けができる
      return {
        questionList: domesticRealEstateInPersonQuestionList.filter((_, i) => answerList[i] !== null),
        answerList: answerList.filter((x) => x !== null),
      } as SetAnswersValue;
    case ResponseType.OverseasRealEstate_Web:
      const overseas_web_q1 = data.GetCompatibility?.OverseasRealEstateAnswer!.WebAnswer!.Question1;
      const overseas_web_q2 = data.GetCompatibility?.OverseasRealEstateAnswer!.WebAnswer!.Question2;
      const overseas_web_q3 = data.GetCompatibility?.OverseasRealEstateAnswer!.WebAnswer!.Question3;
      const overseas_web_q4 = data.GetCompatibility?.OverseasRealEstateAnswer!.WebAnswer!.Question4;
      const overseas_web_q5 = data.GetCompatibility?.OverseasRealEstateAnswer!.WebAnswer!.Question5;
      const overseas_web_q6 = data.GetCompatibility?.OverseasRealEstateAnswer!.WebAnswer!.Question6;
      const overseas_web_q7 = data.GetCompatibility?.OverseasRealEstateAnswer!.WebAnswer!.Question7;
      const overseas_web_q8 = data.GetCompatibility?.OverseasRealEstateAnswer!.WebAnswer!.Question8;
      const overseas_web_q9: SelectAndTextAnswer = {
        values:
          data.GetCompatibility?.OverseasRealEstateAnswer!.WebAnswer!.Question9?.map((x) =>
            x !== null
              ? { value: x, label: CorporatePurposeCompatibilityStrsExcludeOther[x - 1] }
              : { value: -1, label: '' },
          ) ?? [],
        text: '',
      };
      return {
        questionList: overseasRealEstateWebQuestionList,
        answerList: [
          overseas_web_q1,
          overseas_web_q2,
          overseas_web_q3,
          overseas_web_q4,
          overseas_web_q5,
          overseas_web_q6,
          overseas_web_q7,
          overseas_web_q8,
          overseas_web_q9,
        ],
      } as SetAnswersValue;
    case ResponseType.OverseasRealEstate_InPerson:
      const overseas_inPerson_q1 = data.GetCompatibility?.OverseasRealEstateAnswer!.InPersonAnswer!.Question1Str;
      const overseas_inPerson_q2 = data.GetCompatibility?.OverseasRealEstateAnswer!.InPersonAnswer!.Question2Str;
      const overseas_inPerson_q3 = data.GetCompatibility?.OverseasRealEstateAnswer!.InPersonAnswer!.Question3Str;
      const overseas_inPerson_q4 = data.GetCompatibility?.OverseasRealEstateAnswer!.InPersonAnswer!.Question4Str;
      const overseas_inPerson_q5 = data.GetCompatibility?.OverseasRealEstateAnswer!.InPersonAnswer!.Question5Str;
      const overseas_inPerson_q6 = data.GetCompatibility?.OverseasRealEstateAnswer!.InPersonAnswer!.Question6Str;
      const overseas_inPerson_q7 = data.GetCompatibility?.OverseasRealEstateAnswer!.InPersonAnswer!.Question7Str;
      const overseas_inPerson_q8 = data.GetCompatibility?.OverseasRealEstateAnswer!.InPersonAnswer!.Question8Str;
      const overseas_inPerson_q9 = data.GetCompatibility?.OverseasRealEstateAnswer!.InPersonAnswer!.Question9Str;
      const overseas_inPerson_q10 = `${
        data.GetCompatibility?.OverseasRealEstateAnswer!.InPersonAnswer!.Question10Str
      }\n${data.GetCompatibility?.OverseasRealEstateAnswer!.InPersonAnswer!.Question10OtherText}`;
      return {
        questionList: overseasRealEstateInPersonQuestionList,
        answerList: [
          overseas_inPerson_q1,
          overseas_inPerson_q2,
          overseas_inPerson_q3,
          overseas_inPerson_q4,
          overseas_inPerson_q5,
          overseas_inPerson_q6,
          overseas_inPerson_q7,
          overseas_inPerson_q8,
          overseas_inPerson_q9,
          overseas_inPerson_q10,
        ],
      } as SetAnswersValue;
  }
};

type SetAnswersValue = {
  questionList: CompatibilityCheckQuestion[];
  answerList: CompatibilityCheckAnswer[];
};

export const useCompatibilityStartConfirm = (
  setAnswers: React.Dispatch<React.SetStateAction<CompatibilityCheckQuestionToAnswer[]>>,
  conditionType: CompatibilityCheckConditionType,
  assetType: ProductType,
) => {
  /*
  リースファンド{web, 対面}
  国内不動産{個人, 法人}
  海外不動産{法人}  
  */
  const [compatibility, _] = useGetCompatibilityQuery({
    variables: {
      conditionType: conditionType,
      assetType: assetType,
    },
    requestPolicy: 'network-only',
  });
  const [isEditing, setIsEditing] = useState<boolean>(false);
  useEffect(() => {
    const responseType = getValidResponseType(compatibility.data);

    if (responseType == null) {
      return;
    }

    //初期化
    setAnswers([]);

    const { questionList, answerList } = createSetAnswersValue(compatibility.data!, responseType);

    for (let i: number = 0; i < answerList.length; i++) {
      setAnswers((state) => [
        ...state,
        {
          question: questionList[i],
          answer: answerList[i],
        },
      ]);
    }
  }, [compatibility]);

  const navigateToEditPage = () => {
    //初期化
    setAnswers([]);

    //修正ページへ遷移
    setIsEditing(true);
  };

  return {
    compatibility,
    isEditing,
    navigateToEditPage,
  };
};
